import { Search } from 'lucide-react';
import { ChangeEvent, useState } from 'react';
export interface FilterOption {
  label: string;
  value: string;
}

export interface FilterConfig {
  key: string;
  label?: string;
  options: FilterOption[];
  defaultValue?: string;
}

interface SearchFilterProps<T> {
  placeholder?: string;
  data: T[];
  onFilter: (filtered: T[]) => void;
  keys: (keyof T)[];
  componentName?: string;
  statusOptions?: string[];
  projectOptions?: string[];
  registrationOptions?: string[];
  typesOptions?: string[];
  audienceOptions?: string[];
  creationDateOptions?: string[];
}

const SearchFilter = <T extends Record<string, any>>({
  placeholder = 'Search...',
  data,
  onFilter,
  keys,
  componentName,
  statusOptions = ['All Status', 'Active', 'Inactive'],
  projectOptions,
  registrationOptions,
  typesOptions,
  audienceOptions,
  creationDateOptions,
}: SearchFilterProps<T>) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [status, setStatus] = useState('All Status');
  const [project, setProject] = useState('All Projects');
  const [registration, setRegistration] = useState(
    registrationOptions ? registrationOptions[0] : '',
  );
  const [type, setType] = useState('All Types');
  const [audience, setAudience] = useState('All Audiences');
  const [creationDate, setCreationDate] = useState(
    creationDateOptions ? creationDateOptions[0] : '',
  );

  const handleFilter = (
    searchValue: string,
    selectedStatus: string,
    selectedProject: string,
    selectedRegistration: string,
  ) => {
    const value = searchValue.toLowerCase();
    const filtered = data.filter((item) => {
      const matchesSearch = keys.some((key) => String(item[key]).toLowerCase().includes(value));

      const matchesStatus =
        componentName === 'AdminUsers'
          ? selectedStatus === 'All Status' ||
            (selectedStatus === 'Active' && item['isActive'] === true) ||
            (selectedStatus === 'Inactive' && item['isActive'] === false)
          : selectedStatus === 'All Status' || item['status'] === selectedStatus;

      const matchesProject =
        selectedProject === 'All Projects' || item['project'] === selectedProject;

      const matchesRegistration =
        !registrationOptions ||
        selectedRegistration === registrationOptions[0] ||
        String(item['registration']) === selectedRegistration;

      return matchesSearch && matchesStatus && matchesProject && matchesRegistration;
    });

    onFilter(filtered);
  };

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    handleFilter(value, status, project, registration);
  };

  const handleStatusChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setStatus(value);
    handleFilter(searchTerm, value, project, registration);
  };

  const handleProjectChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setProject(value);
    handleFilter(searchTerm, status, value, registration);
  };

  const handleRegistrationChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setRegistration(value);
    handleFilter(searchTerm, status, project, value);
  };

  const handleTypeChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setType(value);
    handleFilter(searchTerm, status, project, registration);
  };

  const handleAudienceChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setAudience(value);
    handleFilter(searchTerm, status, project, registration);
  };

  const handleCreationDateChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setCreationDate(value);
    handleFilter(searchTerm, status, project, registration);
  };

  return (
    <div className="flex items-center justify-center flex-wrap gap-2 rounded-md mb-3 text-[#4A082D] px-1 py-1 bg-[#F2EBEE] shadow">
      {/* Search Input */}
      <div className="flex items-center justify-center flex-grow  bg-[#F2EBEE] rounded-sm text-[#4A082D]">
        <Search className="text-[#4A082D] ml-2 h-4 w-4" />
        <input
          type="text"
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder={placeholder}
          className="flex-1 px-2 py-2 text-sm focus:outline-none bg-transparent placeholder:text-[#4A082D] text-[#4A082D]"
        />
      </div>

      {creationDateOptions && (
        <select
          value={creationDate}
          onChange={handleCreationDateChange}
          className="flex px-3 py-2 border bg-[#F2EBEE] border-[#4A082D1A] text-sm rounded-md text-[#4A082D]"
        >
          {creationDateOptions?.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      )}

      {/* Registration Date Dropdown (only if passed) */}
      {registrationOptions && (
        <select
          value={registration}
          onChange={handleRegistrationChange}
          className="px-3 py-2 border bg-[#F2EBEE] border-[#4A082D1A] rounded-md text-[#4A082D] text-sm"
        >
          {registrationOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      )}

      {/* Status Dropdown */}
      <select
        value={status}
        onChange={handleStatusChange}
        className="flex px-3 py-2 bg-[#F2EBEE] rounded-md focus:outline-none text-sm text-[#4A082D] border border-[#4A082D1A]"
      >
        {statusOptions.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>

      {/* Types Dropdown */}
      {typesOptions && (
        <select
          value={type}
          onChange={handleTypeChange}
          className="flex px-3 py-2 border bg-[#F2EBEE] border-[#4A082D1A] text-sm rounded-md text-[#4A082D]"
        >
          {typesOptions?.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      )}

      {/* Audiences Dropdown */}
      {audienceOptions && (
        <select
          value={audience}
          onChange={handleAudienceChange}
          className="flex px-3 py-2 border bg-[#F2EBEE] border-[#4A082D1A] rounded-md text-sm text-[#4A082D] "
        >
          {audienceOptions?.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      )}

      {/* Project Dropdown */}
      {projectOptions && (
        <select
          value={project}
          onChange={handleProjectChange}
          className="flex px-3 py-2 border bg-[#F2EBEE] border-[#4A082D1A] text-sm rounded-md text-[#4A082D]"
        >
          {projectOptions?.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      )}
    </div>
  );
};

export default SearchFilter;
